import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Slider,
} from "@mui/material";
import { useDispatch } from "react-redux";

import { generatePeerToPeerReportAPI } from "../../../services/resumeService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import tokens from "../../theme/tokens";

const { color, font } = tokens;

/**
 * Peer-to-peer rescoring: the model re-reads the top N candidates against each other
 * rather than against the job spec alone, producing the P2P score/rank the table shows.
 *
 * It's a re-scoring pass over N resumes, so it costs real time and money — hence the
 * explicit top-N choice rather than running it over the whole list automatically.
 */
const PeerCompareDialog = ({ open, onClose, jobId, candidateCount, onDone }) => {
  const dispatch = useDispatch();
  const [topK, setTopK] = useState(5);
  const [loading, setLoading] = useState(false);

  const max = Math.max(2, Math.min(candidateCount || 2, 25));

  const handleGenerate = async () => {
    setLoading(true);
    try {
      await generatePeerToPeerReportAPI(jobId, topK);
      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: `Peer comparison started for the top ${topK}. Scores appear in the P2P column once it finishes.`,
        })
      );
      onDone?.();
      onClose();
    } catch (e) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: e.message || "Failed to generate the peer comparison.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ font: `600 15px ${font.sans}` }}>Peer comparison</DialogTitle>
      <DialogContent>
        <Box sx={{ font: `400 12.5px/1.65 ${font.sans}`, color: color.textSecondary, mb: 3 }}>
          Re-scores your top candidates against <strong>each other</strong> rather than
          against the job spec alone. Fills the P2P column.
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ flex: "none", font: `500 12px ${font.sans}`, color: color.textSecondary }}>
            Top
          </Box>
          <Slider
            value={topK}
            onChange={(_, v) => setTopK(v)}
            min={2}
            max={max}
            step={1}
            marks
            sx={{
              color: color.indigo,
              "& .MuiSlider-rail": { height: 5, bgcolor: color.borderRow, opacity: 1 },
              "& .MuiSlider-track": { height: 5, border: "none" },
              "& .MuiSlider-thumb": {
                width: 13,
                height: 13,
                bgcolor: "#fff",
                border: `2px solid ${color.indigo}`,
              },
            }}
          />
          <Box
            sx={{
              flex: "none",
              width: 28,
              textAlign: "right",
              font: `600 13px ${font.mono}`,
              color: color.text,
            }}
          >
            {topK}
          </Box>
        </Box>

        <Box sx={{ mt: 1, font: `400 11px ${font.mono}`, color: color.textMuted }}>
          {topK} of {candidateCount} candidates will be re-scored.
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleGenerate} disabled={loading}>
          {loading ? "Starting…" : "Run comparison"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PeerCompareDialog;
